import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';
import { useAuthStore } from '../state/authStore.ts';

/**
 * FAQ page with FAQPage JSON-LD structured data. Google rewards these with "People also ask"
 * rich results, often lifting organic CTR by 20-30% on long-tail compliance queries.
 *
 * Schema rules:
 *   - Every Q&A in the visible UI must also appear in the JSON-LD (1:1 mapping)
 *   - Answers must be plain text, no HTML, no marketing fluff that doesn't appear in the page
 *   - Don't link out to other domains from inside the answer text — Google penalizes that
 *
 * If you add a Q&A, update BOTH arrays. The schema generator below derives directly from
 * the FAQ data array to keep them in sync.
 */

type Qa = {
  q: string;
  a: string;
  /** Optional JSX-only addendum displayed under the answer (e.g. links). NOT included in the
      JSON-LD because schema answers must be text-only. */
  more?: React.ReactNode;
};

const FAQ: Qa[] = [
  {
    q: 'Is the EAA Proof report enough to satisfy the European Accessibility Act?',
    a: "The report and accessibility statement we generate are a strong automated first-pass and match the European Commission's model accessibility statement template (Implementing Decision (EU) 2018/1523). For low-risk small business sites, this is typically sufficient. For regulated industries (banking, ticketing, e-commerce above €2M turnover) we strongly recommend pairing our report with a manual review by a certified accessibility expert before publishing the statement.",
  },
  {
    q: 'What is WCAG 2.1 AA and how does it relate to the EAA?',
    a: 'WCAG 2.1 (Web Content Accessibility Guidelines) is the international technical standard for web accessibility. Level AA is the practical compliance target referenced by the EU harmonised standard EN 301 549, which is what the European Accessibility Act (Directive 2019/882) expects you to meet. EAA Proof scans for WCAG 2.1 AA conformance.',
  },
  {
    q: 'Does the EAA apply to my business?',
    a: 'The European Accessibility Act applies to businesses placing covered products and services on the EU market after 28 June 2025. Covered services include e-commerce, e-banking, e-ticketing, e-books, and any consumer-facing digital service. Micro-enterprises (under 10 employees AND under €2M turnover) providing services are exempt — but only from the services side; products are not exempt. If you have any EU consumers and are not a micro-enterprise, assume it applies.',
  },
  {
    q: 'What does a single scan cost and what do I get?',
    a: 'A single scan is €29 one-time. You get a downloadable PDF audit report covering up to 10 same-origin pages, a ready-to-publish EU model accessibility statement HTML (Implementing Decision (EU) 2018/1523), and plain-English remediation steps for each violation grounded in your actual page HTML.',
    more: (
      <p className="muted small">
        See <Link to="/pricing">pricing</Link> for our €9/mo (5 scans) and €49/mo agency (50
        scans + branding) plans.
      </p>
    ),
  },
  {
    q: 'What if my site has more than 10 pages?',
    a: 'We crawl up to 10 same-origin pages per scan, prioritising the homepage, sitemap entries, and the most commonly-linked pages. For larger sites the agency plan (50 scans/month) is the typical fit — run multiple scans against different subsections.',
  },
  {
    q: 'How is EAA Proof different from AccessiBe, UserWay, or Siteimprove?',
    a: 'AccessiBe and UserWay sell JavaScript overlay widgets that claim to "auto-fix" accessibility issues. Accessibility bodies including WebAIM, the W3C, and the European Disability Forum have published positions that overlays do not, on their own, remediate accessibility issues and can interfere with screen readers; the US DOJ has stated that overlays alone do not ensure compliance. EAA Proof scans your actual code and produces remediation guidance your engineers can apply. Siteimprove is enterprise-priced and targets large teams; EAA Proof is built for SMBs and agencies that need a documented, standards-based audit without a six-figure contract.',
    more: (
      <p className="muted small">
        Detailed comparisons:{' '}
        <Link to="/vs/accessibe">vs AccessiBe</Link>,{' '}
        <Link to="/vs/userway">vs UserWay</Link>,{' '}
        <Link to="/vs/siteimprove">vs Siteimprove</Link>.
      </p>
    ),
  },
  {
    q: 'Can I use EAA Proof on a client website (as an agency)?',
    a: 'Yes. The agency plan (€49/month) is specifically built for this: 50 scans per month and the ability to add your agency branding to the generated PDF reports. You retain full ownership of the deliverables you hand to your clients.',
  },
  {
    q: 'How long does a scan take?',
    a: 'Typically 60 to 120 seconds end-to-end. Time scales with the number of pages crawled and the complexity of the JavaScript on those pages. We use real Chromium via Microsoft Playwright, so the scan sees the same DOM a real user would see — including content rendered by client-side JavaScript.',
  },
  {
    q: 'What accessibility engine does EAA Proof use?',
    a: 'We run axe-core 4.10 (the open-source de-facto standard for automated accessibility testing) inside a real Chromium browser via Microsoft Playwright. axe-core is the same engine that powers Google Lighthouse and is referenced in W3C accessibility evaluation resources.',
  },
  {
    q: 'Are the AI-generated remediations safe to apply blindly?',
    a: 'The remediations are produced by Anthropic Claude Sonnet with five layers of anti-hallucination guards — including byte-for-byte echo verification and substring grounding that drops AI output if it does not literally appear in your HTML. They are accurate first-pass advice but should be reviewed by a developer before deployment, especially for complex interactive widgets, ARIA roles, and dynamic content patterns.',
  },
  {
    q: 'Where is my data stored and processed?',
    a: 'All scan data is hosted on Hetzner servers in the EU (Helsinki / Falkenstein) and downloadable reports are served from Cloudflare R2 (EU jurisdiction). The page HTML we send to Anthropic for remediation analysis is transmitted via TLS, never stored by Anthropic per their commercial API terms, and contains only the violation snippets — not your entire site.',
    more: (
      <p className="muted small">
        Full data handling details in our <Link to="/privacy">privacy policy</Link>.
      </p>
    ),
  },
  {
    q: 'Do I need to create an account to try EAA Proof?',
    a: 'No. You can view a complete sample report at /sample with no sign-up required. To run a scan against your own site you do need to sign in (Google OAuth one click or email and password) so we can store your scan history and bill correctly.',
  },
  {
    q: 'Can I get a refund?',
    a: 'Yes. We offer a 7-day money-back guarantee on every purchase, no questions asked. EU consumers also retain the statutory 14-day right of withdrawal until they start their first scan.',
    more: (
      <p className="muted small">
        See the full <Link to="/refund-policy">refund policy</Link>.
      </p>
    ),
  },
  {
    q: 'Is the generated accessibility statement legally binding?',
    a: "The statement template is structured to match the European Commission's model accessibility statement (Implementing Decision (EU) 2018/1523) — the exact format EU regulators expect. The legal weight depends on how accurately the underlying scan reflects your site state and whether you fix the identified issues before publishing. We recommend treating the auto-generated statement as a strong starting point and having it reviewed by your legal or compliance team before going live on a regulated business.",
  },
  {
    q: 'What happens if a scan fails?',
    a: 'Failed scans never debit a credit from your account. If the failure was due to our scanner crashing, a timeout, or an error in the AI remediation step, you can immediately re-attempt. If the failure was due to your site (e.g. password-protected, blocking our user agent, hitting a 5xx error), we surface the specific error so you can fix it and re-run.',
  },
];

export default function FaqPage() {
  useSeo({
    title: 'Frequently asked questions',
    canonicalPath: '/faq',
    description:
      'Answers to common questions about EAA Proof: how the scan works, what the report covers, pricing, EU Accessibility Act applicability, and how we compare to overlay vendors.',
  });

  const user = useAuthStore(s => s.user);
  const scanCtaTarget = user ? '/' : '/sign-in';

  useEffect(() => {
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'faq-jsonld';
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: a,
        },
      })),
    });
    document.head.appendChild(ld);
    return () => {
      document.getElementById('faq-jsonld')?.remove();
    };
  }, []);

  return (
    <div className="page faq-page">
      <header>
        <h1>Frequently asked questions</h1>
        <p className="muted">
          Quick answers about EAA Proof. If your question isn't here, email{' '}
          <a href="mailto:support@eaaproof.com">support@eaaproof.com</a> and we'll answer within
          1 business day.
        </p>
      </header>

      <div className="faq-list">
        {FAQ.map((item) => (
          <details key={item.q} className="faq-item">
            <summary>{item.q}</summary>
            <div className="faq-answer">
              <p>{item.a}</p>
              {item.more}
            </div>
          </details>
        ))}
      </div>

      <section className="faq-cta">
        <h2>Ready to scan your site?</h2>
        <p>
          Run a single scan for €29 — get a downloadable PDF audit + a ready-to-publish
          accessibility statement HTML in under 2 minutes.
        </p>
        <Link to={scanCtaTarget} className="cta">
          Start a scan
        </Link>
      </section>
    </div>
  );
}
